// The Cowork text on the skill page. Instance C owns these words; Instance B owns
// the rendering (handover-B, phase 2 item 6).
//
// SOURCE: atlas-billing-ops 96e15dd (merge of C's #11),
// `.claude/skills/submit-queue/SKILL.md`, section "How to run". The template below
// equals that section exactly (checked 2026-09-13), except that the section's italic
// first paragraph is left out because it is addressed to Atlas, not the reader. When
// C changes the section, copy it again and update the SHA here and in
// SKILL_TEXT_SOURCE; QueuePhase2.test.js pins the placeholders, not the prose.
//
// Placeholders Atlas fills: {practice_id}, {portal_key}, {atlas_base_url}.
// The API key is never part of this text — only where it goes.

export const SKILL_TEXT_SOURCE = {
  repo: "atlas-billing-ops",
  path: ".claude/skills/submit-queue/SKILL.md",
  section: "How to run",
  commit: "96e15dd",
};

export const SKILL_TEXT_TEMPLATE = `**You need:** a \`queue:work\` API key for this practice in \`ATLAS_API_KEY\`
(environment, never chat or a command line), \`ATLAS_BASE_URL={atlas_base_url}\`,
the atlas-billing-ops checkout, and the dedicated Chrome profile below, logged in
to the portal by a person.

**One-time Chrome setup.** Chrome refuses remote control of your everyday
profile, so this skill uses its own profile. Quit nothing; open Terminal and run:

\`\`\`bash
"/Applications/Google Chrome.app/Contents/MacOS/Google Chrome" \\
  --remote-debugging-port=9222 --user-data-dir="$HOME/.atlas-chrome/submit-queue"
\`\`\`

On Windows:

\`\`\`bat
"C:\\Program Files\\Google\\Chrome\\Application\\chrome.exe" --remote-debugging-port=9222 --user-data-dir="%USERPROFILE%\\.atlas-chrome\\submit-queue"
\`\`\`

A new Chrome window opens. Log in to the portal there, as you normally would,
including any code or CAPTCHA. Leave the window open. Next time, run the same
command; the profile remembers the login until the portal expires it. The
debugging port only listens on this Mac, and the skill refuses any other address.

**1. Dry run (always first).**

\`\`\`bash
cd atlas-billing-ops
python -m ops.queue.run --practice {practice_id} --portal {portal_key} --dry-run --batch 1
\`\`\`

Watch the Chrome window: the skill opens a tab, fills the claim form, and stops
before submit. Nothing is sent. The claim goes back in the queue.

**2. Supervised live run (Jordan or David present).** Read the runbook version
in Atlas first and note its version number, then:

\`\`\`bash
python -m ops.queue.run --practice {practice_id} --portal {portal_key} --live --batch 5 \\
  --runbook-version <the version you read> --operator <your email>
\`\`\`

The run refuses if the version you read is not Atlas's latest, if the runbook
still has an \`UNKNOWN\` in it, or if the batch is over 5.

**3. Reading the result.** The last lines are a summary:

| Line | Meaning |
|---|---|
| \`submitted N\` | Sent through the portal, reference captured, recorded in Atlas. |
| \`dry_run_ok N\` | Dry run: filled to the submit control, released. |
| \`failed N\` | The portal did not do what the runbook expected. Back in the queue with the reason. |
| \`needs_human N\` | A person has to act — the reason is on the line. |
| \`skipped N\` | Someone else had the claim leased. |
| \`unreported N\` | **Sent, but Atlas has not recorded it.** See "If a submission is unreported". |
| \`unconfirmed N\` | **Submit was pressed and no reference appeared.** The claim is held in Atlas, not re-queued. See "If submit was pressed but no reference appeared". |

Exit codes: \`0\` done · \`1\` refused or failed · \`2\` needs a human · \`3\` a claim
may have been sent and Atlas does not know — fix that before anything else.

**If a submission is unreported** (exit 3):

\`\`\`bash
python -m ops.queue.run --practice {practice_id} --report-pending
\`\`\`

This posts the saved reference to Atlas. If Atlas refuses because someone else
now holds the claim, the command prints the claim id and reference. Mark that
claim "I submitted" in Atlas's queue page with that reference, then run
\`--clear-unconfirmed <claim id>\`. Until the journal is clear, the skill will not
lease anything for this practice.

**If submit was pressed but no reference appeared** (exit 3, \`unconfirmed\`), the
run has stopped and **held** the claim in Atlas. A held claim never expires and
cannot be leased again; only a person clears it. Open the portal and look for the
claim, then settle it on Atlas's queue page:

- **It went:** press "I submitted" on the held row with the portal's reference.
  For Claim.MD that is the claim id, and only that. Then clear the local journal:
  \`python -m ops.queue.run --practice {practice_id} --clear-unconfirmed <claim id>\`
- **It did not go:** press "Not sent" on the held row, with a reason. That puts
  it back in the queue. **Never record "I submitted" for a claim that did not
  go.** Then:
  \`python -m ops.queue.run --practice {practice_id} --clear-unconfirmed <claim id> --not-sent\`

If the run printed that Atlas **could not hold** the claim, only this run's lease
protects it, for **at most 30 minutes**. Record what you find from here instead:
\`--portal {portal_key} --confirm-unconfirmed <claim id> --ref <reference>\` if it
went, or \`--clear-unconfirmed <claim id> --not-sent\` if it did not.

\`--clear-unconfirmed\` without \`--not-sent\` only works once Atlas shows the claim as
neither queued, leased nor held.
`;

const PLACEHOLDERS = ["practice_id", "portal_key", "atlas_base_url"];

// Replace every placeholder; a value we do not have becomes a visible <marker>
// rather than an empty string, so a pasted command cannot silently lose an argument.
export function fillSkillText({ practiceId, portalKey, baseUrl }) {
  const values = {
    practice_id: practiceId != null ? String(practiceId) : "<practice id>",
    portal_key: portalKey || "<portal key>",
    atlas_base_url: baseUrl || "<atlas base url>",
  };
  return PLACEHOLDERS.reduce(
    (text, key) => text.split(`{${key}}`).join(values[key]),
    SKILL_TEXT_TEMPLATE,
  );
}
